// Shared sticky-note palette. Used by:
//   - To-Do stickies (pages/Todos.jsx)
//   - Client colour codes (pages/Clients.jsx)
// A "colour" value can be either a preset id (e.g. 'amber') or a hex string ('#ffcc00').
// stickyColorFor(...) always returns a full palette object {bg, border, tape, ink}.

export const STICKY_COLORS = [
  { id: 'pink',  bg: '#fde7ef', border: '#f4c1d5', tape: '#f9a8c8', ink: '#7a2946' },
  { id: 'lilac', bg: '#ece2f5', border: '#d3c1e1', tape: '#b79ad6', ink: '#4b326e' },
  { id: 'mint',  bg: '#dff3e8', border: '#b3dcc4', tape: '#7ec89a', ink: '#26543a' },
  { id: 'amber', bg: '#fdefc7', border: '#f5d879', tape: '#e6bf3d', ink: '#6b4d10' },
  { id: 'sky',   bg: '#dcecfa', border: '#a9c8eb', tape: '#78acdd', ink: '#1e3a5b' },
  { id: 'peach', bg: '#ffe4d1', border: '#f5c199', tape: '#e79a63', ink: '#7a3d10' },
];

// ---------- hex helpers ----------
const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;
export const isHexColor = (v) => typeof v === 'string' && HEX_RE.test(v.trim());
export const normalizeHex = (v) => {
  if (!isHexColor(v)) return null;
  let s = v.trim().replace(/^#/, '');
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  return '#' + s.toLowerCase();
};

const hexToRgb = (hex) => {
  const h = normalizeHex(hex) || '#000000';
  return { r: parseInt(h.slice(1, 3), 16), g: parseInt(h.slice(3, 5), 16), b: parseInt(h.slice(5, 7), 16) };
};
const rgbToHex = (r, g, b) => '#' + [r, g, b].map((n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')).join('');
const mix = (hex, other, t) => {
  // t=0 -> hex, t=1 -> other
  const a = hexToRgb(hex), c = hexToRgb(other);
  return rgbToHex(a.r + (c.r - a.r) * t, a.g + (c.g - a.g) * t, a.b + (c.b - a.b) * t);
};

// Build a sticky palette from any hex tape colour
export const paletteFromHex = (hex) => {
  const tape = normalizeHex(hex) || '#f9a8c8';
  return {
    id: tape,
    tape,
    bg: mix(tape, '#ffffff', 0.82),      // very light bg
    border: mix(tape, '#ffffff', 0.55),  // soft border
    ink: mix(tape, '#000000', 0.55),     // readable text
  };
};

export const stickyColorFor = (value) => {
  if (isHexColor(value)) return paletteFromHex(value);
  return STICKY_COLORS.find((c) => c.id === value) || STICKY_COLORS[0];
};

// Deterministic fallback if a client hasn't picked a colour yet
export const clientColorAuto = (client) => {
  if (!client) return 'lilac';
  const src = String(client.id || client.name || '');
  let h = 0;
  for (let i = 0; i < src.length; i++) h = (h * 31 + src.charCodeAt(i)) >>> 0;
  return STICKY_COLORS[h % STICKY_COLORS.length].id;
};

// A client can store either a preset id ('amber') or a hex string ('#ff0000')
export const clientColorFor = (client) => (client && client.color) || clientColorAuto(client);
